import React, { useEffect, useMemo, useRef, useState } from 'react';
import L from 'leaflet';
import { motion } from 'framer-motion';
import { ChevronLeftIcon, ChevronRightIcon, MapPinIcon, RadioIcon, ThumbsUpIcon, UsersIcon } from 'lucide-react';
import { profileCoordinates, publicReportStatusLabels, publicReports, reportCategories, type PublicReport, type PublicReportCategory } from '../../data/demandes';
type Filter = 'all' | PublicReportCategory;
const filters: Filter[] = ['all', 'danger', 'voirie', 'eclairage', 'arbre'];
const statusStyles: Record<PublicReport['status'], string> = {
  nouveau: 'bg-ville-lime text-ville-navy',
  'in-progress': 'bg-ville-indigo-soft text-ville-indigo',
  resolue: 'bg-slate-100 text-slate-500'
};
function pinIcon(report: PublicReport, active: boolean): L.DivIcon {
  const color = reportCategories[report.category].color;
  const size = active ? 34 : 24;
  return L.divIcon({
    className: 'atna-pin',
    html: `<span style="background:${color};width:${size}px;height:${size}px" class="block rounded-full border-[3px] border-white shadow-md shadow-ville-navy/30 ${active ? 'ring-2 ring-ville-navy/20' : ''}"></span>`,
    iconSize: [size, size],
    iconAnchor: [size / 2, size / 2]
  });
}
export function PublicReportsMap() {
  const [filter, setFilter] = useState<Filter>('all');
  const [selectedId, setSelectedId] = useState<string>(publicReports[0].id);
  const [confirmedIds, setConfirmedIds] = useState<string[]>([]);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<L.Map | null>(null);
  const layerRef = useRef<L.LayerGroup | null>(null);
  const visible = useMemo(() => filter === 'all' ? publicReports : publicReports.filter(report => report.category === filter), [filter]);
  const selected = visible.find(report => report.id === selectedId) ?? visible[0];
  const selectedIndex = selected ? visible.indexOf(selected) : -1;
  const newCount = publicReports.filter(report => report.status === 'nouveau').length;
  useEffect(() => {
    if (!containerRef.current || mapRef.current) return;
    const map = L.map(containerRef.current, {
      center: profileCoordinates,
      zoom: 14,
      zoomControl: false,
      scrollWheelZoom: false,
      attributionControl: true
    });
    map.attributionControl.setPrefix(false);
    // OpenStreetMap tiles: free and no API key needed.
    L.tileLayer('https://tile.openstreetmap.org/{z}/{x}/{y}.png', {
      maxZoom: 19,
      attribution: '© OpenStreetMap'
    }).addTo(map);
    L.control.zoom({
      position: 'topright'
    }).addTo(map);
    L.circleMarker(profileCoordinates, {
      radius: 7,
      color: '#ffffff',
      weight: 3,
      fillColor: '#04302f',
      fillOpacity: 1
    }).bindTooltip('Your address').addTo(map);
    layerRef.current = L.layerGroup().addTo(map);
    mapRef.current = map;
    return () => {
      map.remove();
      mapRef.current = null;
      layerRef.current = null;
    };
  }, []);
  useEffect(() => {
    const layer = layerRef.current;
    if (!layer) return;
    layer.clearLayers();
    visible.forEach(report => {
      L.marker([report.lat, report.lng], {
        icon: pinIcon(report, report.id === selected?.id),
        keyboard: true,
        title: `${reportCategories[report.category].label} — ${report.title}`,
        alt: `${reportCategories[report.category].label} — ${report.title}`,
        zIndexOffset: report.id === selected?.id ? 500 : 0
      }).on('click', () => setSelectedId(report.id)).on('keypress', () => setSelectedId(report.id)).addTo(layer);
    });
  }, [visible, selected?.id]);
  useEffect(() => {
    if (!mapRef.current || !selected) return;
    mapRef.current.panTo([selected.lat, selected.lng], {
      animate: true,
      duration: 0.3
    });
  }, [selected?.id]);
  const step = (direction: -1 | 1) => {
    if (visible.length === 0) return;
    const next = (selectedIndex + direction + visible.length) % visible.length;
    setSelectedId(visible[next].id);
  };
  const toggleConfirm = (id: string) => {
    setConfirmedIds(current => current.includes(id) ? current.filter(item => item !== id) : [...current, id]);
  };
  return <section aria-labelledby="carte-publique-titre"
  /* `isolate` keeps the Leaflet layers (controls, floating card) in their own stacking
     context, otherwise they sit on top of the ATNA dialog. */ className="relative isolate z-0 overflow-hidden rounded-3xl border border-slate-200 bg-white" data-id="element-809" data-mp="2fj2e9cawfsna">
      <div className="px-5 pb-4 pt-5 sm:px-6" data-id="element-810" data-mp="21j1fq3i3wxaz">
        <div className="flex items-start justify-between gap-4" data-id="element-811" data-mp="21m5o7d2luf7g">
          <div data-id="element-812" data-mp="1wl88r88zbz9n">
            <h2 id="carte-publique-titre" className="text-xl font-extrabold tracking-tight text-ville-navy" data-id="element-813" data-mp="1wmsv4al54bwf">
              Reports around you
            </h2>
            <p className="mt-1 text-sm text-slate-500" data-id="element-814" data-mp="3qxpta82folal">
              {publicReports.length} active reports · {newCount} new today
            </p>
          </div>
          <span className="inline-flex shrink-0 items-center gap-1.5 rounded-full bg-ville-mist px-3 py-1 text-xs font-bold text-ville-deep" data-id="element-815" data-mp="18pee1e8je8h5">
            <RadioIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-816" data-mp="1dn6i16mghl1p" />
            Live
          </span>
        </div>

        <ul className="mt-4 flex flex-wrap gap-2" data-id="element-817" data-mp="1ifpug09eqa4x">
          {filters.map(option => {
          const active = filter === option;
          const label = option === 'all' ? 'All' : reportCategories[option].label;
          return <li key={option} data-id="element-818" data-mp="upc0g479i17p">
                <button type="button" onClick={() => setFilter(option)} aria-pressed={active} className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-bold transition-colors duration-150 ease-ville focus:outline-none focus-visible:ring-2 focus-visible:ring-ville-indigo ${active ? 'border-ville-navy bg-ville-navy text-white' : 'border-slate-200 bg-white text-slate-600 hover:border-ville-indigo hover:text-ville-indigo'}`} data-id="element-819" data-mp="uqn1hw9bouv4">
                  {option !== 'all' && <span className="h-2 w-2 rounded-full" style={{
                backgroundColor: reportCategories[option].color
              }} aria-hidden="true" data-id="element-820" data-mp="p1wx85qknh8k" />}
                  {label}
                </button>
              </li>;
        })}
        </ul>
      </div>

      <div className="relative" data-id="element-821" data-mp="3ngxvpey53yqx">
        <div ref={containerRef} role="application" aria-label="Map of public reports in Bonnyville" className="h-[300px] w-full bg-ville-mist sm:h-[380px]" data-id="element-822" data-mp="629ajsgexl8o" />

        {selected ? <motion.div key={selected.id} initial={{
        opacity: 0,
        y: 12
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.2,
        ease: [0.23, 1, 0.32, 1]
      }} className="pointer-events-auto absolute inset-x-3 bottom-3 z-[900] rounded-2xl border border-slate-200 bg-white p-4 shadow-xl shadow-ville-navy/10 sm:inset-x-4" data-id="element-823" data-mp="39qb7whtx3t9m">
            <div className="flex items-start gap-3" data-id="element-824" data-mp="1batvu1tzio4z">
              <span className="mt-1 h-3 w-3 shrink-0 rounded-full" style={{
            backgroundColor: reportCategories[selected.category].color
          }} aria-hidden="true" data-id="element-825" data-mp="1fbdtd6igztrn" />
              <div className="min-w-0 flex-1" data-id="element-826" data-mp="280l3z3v789t9">
                <div className="flex flex-wrap items-center gap-2" data-id="element-827" data-mp="27za0741r7225">
                  <h3 className="text-[15px] font-extrabold leading-tight text-ville-navy" data-id="element-828" data-mp="1tz8n8q87s46b">{selected.title}</h3>
                  <span className={`rounded-full px-2.5 py-0.5 text-[11px] font-bold ${statusStyles[selected.status]}`} data-id="element-829" data-mp="1yy20chjl9t5y">
                    {publicReportStatusLabels[selected.status]}
                  </span>
                </div>
                <p className="mt-1 flex items-center gap-1.5 text-[13px] text-slate-500" data-id="element-830" data-mp="ni51o1ly7uvj">
                  <MapPinIcon className="h-3.5 w-3.5 shrink-0" aria-hidden="true" data-id="element-831" data-mp="1ebmbmvprvp12" />
                  <span className="truncate" data-id="element-832" data-mp="1a74wxay9s1x5">{selected.address}</span>
                </p>
                <p className="mt-0.5 text-[13px] text-slate-400" data-id="element-833" data-mp="15aeggmijz4b7">
                  {selected.distance} away · {selected.reportedAgo}
                </p>
              </div>
              <div className="flex shrink-0 items-center gap-1" data-id="element-834" data-mp="1xysev3dk02na">
                <button type="button" onClick={() => step(-1)} aria-label="Previous report" className="grid h-8 w-8 place-items-center rounded-full text-slate-400 transition-colors duration-150 ease-ville hover:bg-slate-100 hover:text-ville-navy focus:outline-none focus-visible:ring-2 focus-visible:ring-ville-indigo" data-id="element-835" data-mp="1y03hv0z6d6dc">
                  <ChevronLeftIcon className="h-4 w-4" aria-hidden="true" data-id="element-836" data-mp="2by1y6bx23cvh" />
                </button>
                <span className="text-[11px] font-semibold tabular-nums text-slate-400" data-id="element-837" data-mp="2j97xvlj1zfcy">
                  {selectedIndex + 1}/{visible.length}
                </span>
                <button type="button" onClick={() => step(1)} aria-label="Next report" className="grid h-8 w-8 place-items-center rounded-full text-slate-400 transition-colors duration-150 ease-ville hover:bg-slate-100 hover:text-ville-navy focus:outline-none focus-visible:ring-2 focus-visible:ring-ville-indigo" data-id="element-838" data-mp="2o8je8tficiu0">
                  <ChevronRightIcon className="h-4 w-4" aria-hidden="true" data-id="element-839" data-mp="3752hj23mrcin" />
                </button>
              </div>
            </div>

            <div className="mt-3 flex items-center justify-between gap-3 border-t border-slate-100 pt-3" data-id="element-840" data-mp="3gwoylf2ng5tb">
              <p className="flex items-center gap-1.5 text-[13px] text-slate-500" data-id="element-841" data-mp="3lwl45iqzw5az">
                <UsersIcon className="h-4 w-4 text-slate-400" aria-hidden="true" data-id="element-842" data-mp="3pxdfchjfgk9w" />
                {selected.confirmations + (confirmedIds.includes(selected.id) ? 1 : 0)} people confirm this
              </p>
              <button type="button" onClick={() => toggleConfirm(selected.id)} aria-pressed={confirmedIds.includes(selected.id)} className={`inline-flex items-center gap-2 rounded-full px-4 py-2 text-xs font-bold transition-colors duration-150 ease-ville focus:outline-none focus-visible:ring-2 focus-visible:ring-ville-indigo focus-visible:ring-offset-2 ${confirmedIds.includes(selected.id) ? 'bg-ville-indigo-soft text-ville-indigo' : 'bg-ville-navy text-white hover:bg-ville-navy/90'}`} data-id="element-843" data-mp="30nrowtsver26">
                <ThumbsUpIcon className="h-4 w-4" aria-hidden="true" data-id="element-844" data-mp="3tcgk6c78w260" />
                {confirmedIds.includes(selected.id) ? 'Confirmed' : 'I can confirm this'}
              </button>
            </div>
          </motion.div> : <div className="absolute inset-x-3 bottom-3 z-[900] rounded-2xl border border-slate-200 bg-white p-4 text-sm text-slate-500 shadow-xl shadow-ville-navy/10" data-id="element-845" data-mp="1rfg2bms5bpn3">
            No reports in this category right now.
          </div>}
      </div>
    </section>;
}